import { useState, useEffect } from 'react';
import { Card, Col, Container, Dropdown, Offcanvas, Row, Toast, ToastContainer } from 'react-bootstrap';

import { Link } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import useAdvanceTable from 'hooks/useAdvanceTable';
import {
    faPlus
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'providers/AuthProvider'
import apiClient, { handleAPIError } from 'helpers/apiService';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Button from 'components/base/Button';
import Template from './components/Template';
import RevealDropdown, { RevealDropdownTrigger } from 'components/base/RevealDropdown';
import { AssessmentTemplate } from './components/DataDefinitions';
import AssessmentBuilder from './components/AssessmentBuilder';



const Table = () => {
    return (
        <div className="border-top">
            <AdvanceTable
                tableProps={{ className: 'phoenix-table fs-9' }}
                rowClassName="hover-actions-trigger btn-reveal-trigger"
            />
            <AdvanceTableFooter pagination className="py-4" />
        </div>
    );
};



const AdminSkillsTemplates = () => {
    const { t } = useTranslation();

    const breadcrumbItems: PageBreadcrumbItem[] = [
        {
            label: t('Dashboard'),
            url: '/dashboard/home'
        },

        {
            label: t('Skills Assessment Templates') + " (Admin)",
            active: true
        }
    ];


    const auth = useAuth();
    const [loading, setLoading] = useState(true);
    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const [templates, setTemplates] = useState<AssessmentTemplate[]>([]);
    const [template, setTemplate] = useState<AssessmentTemplate>();

    // State to control Toast messages
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');


    const handleSaveTemplate = (message: string) => {
        setToastMessage(message);
        setShowToast(true);
    }

    const ActionDropdownItems = ({ skills_template_id, onDelete, onDuplicate }: {
        skills_template_id: string | number,
        onDelete: (skills_template_id: string | number) => void;
        onDuplicate: (skills_template_id: string | number) => void;
    }) => {
        return (
            <>
                <Dropdown.Item eventKey="1" onClick={() => onDuplicate(skills_template_id)} className="text-info">
                    Duplicate
                </Dropdown.Item>

                <Dropdown.Item eventKey="2" onClick={() => onDelete(skills_template_id)} className="text-danger">
                    Remove
                </Dropdown.Item>
            </>
        );
    };

    const assessmentListTableColumns: ColumnDef<AssessmentTemplate>[] = [
        {
            header: t('AdminSkillsTemplates.table.role'),
            cell: ({ row: { original } }) => {
                const { role } = original;
                return (
                    <Link to="#!" onClick={() => setTemplate(original)} className="fw-semi-bold">
                        {role}
                    </Link>
                );
            },
            meta: {
                cellProps: { className: 'white-space-nowrap py-4' },
                headerProps: { style: { width: '35%' } }
            }
        },

        {
            header: t('AdminSkillsTemplates.table.assessment_type'),
            accessorKey: 'assessment_type',
            meta: {
                cellProps: { className: 'ps-3 text-900 py-4' },
                headerProps: { style: { width: '25%' }, className: 'ps-3' }
            }
        },

        {
            header: 'Date',
            accessorKey: 'inserted_at',
            meta: {
                cellProps: { className: 'ps-3 text-900 py-4' },
                headerProps: { style: { width: '20%' }, className: 'ps-3' }
            }
        },
        {
            id: 'dropdown',
            cell: ({ row: { original } }) => {
                return (
                    <RevealDropdownTrigger>
                        <RevealDropdown>
                            <ActionDropdownItems skills_template_id={original.skills_template_id} onDelete={handleDelete} onDuplicate={handleDuplicate} />
                        </RevealDropdown>
                    </RevealDropdownTrigger>
                );
            },
            meta: {
                cellProps: {
                    className: 'text-end pe-0 ps-4'
                }
            }
        }

    ];

    const table = useAdvanceTable({
        data: templates,
        columns: assessmentListTableColumns,
        pageSize: 10,
        pagination: true,
        sortable: true
    });


    const fetchData = async () => {
        try {
            const response = await apiClient.get(
                '/v1/skills/admin/templates',
                {
                    headers: {
                        'accept': 'application/json',

                    },
                    withCredentials: true,
                }
            );
            setTemplates(response.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };


    const handleDelete = (skills_template_id: string | number) => { }
    const handleDuplicate = (skills_template_id: string | number) => { }

    useEffect(() => {
        fetchData();
    }, []);



    if (loading) {
        return <div>Loading...</div>;
    }



    return (
        <>

            <PageBreadcrumb items={breadcrumbItems} />


            <ToastContainer className="p-3" position='top-center'>
                <Toast onClose={() => setShowToast(false)} show={showToast} delay={3000} autohide>
                    <Toast.Body className=''>{toastMessage}</Toast.Body>
                </Toast>
            </ToastContainer>


            <Offcanvas show={show} onHide={handleClose} backdrop="static" placement="end" style={{ 'width': '50%' }}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className='fs-5' >Assessment Builder</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    {template && <Template title={template.role + " - " + template.inserted_at} data={template} />}
                    <AssessmentBuilder onCancel={handleClose} onSave={handleSaveTemplate} />
                </Offcanvas.Body>
            </Offcanvas>


            <Card className='mt-3 border border-light rounded-5' >
                <Card.Body>
                    <AdvanceTableProvider {...table}>
                        <Row className="g-3 justify-content-between mb-4">
                            <Col xs="auto">
                                <Button
                                    variant="primary"
                                    className="me-4"
                                    startIcon={
                                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                                    }
                                    onClick={handleShow}
                                >
                                    {t('AdminSkillsTemplates.new_template')}
                                </Button>
                            </Col>
                        </Row>
                        <Table />
                    </AdvanceTableProvider>
                </Card.Body>
            </Card>



        </>
    );


};

export default AdminSkillsTemplates;
